import Phaser from "phaser";
import { ICON_SMOOTH_TEXTURE, ICON_TEXTURE } from "../../icons/atlas-texture.ts";
import type { WorldUiContext } from "../hook.ts";
import type { BadgeParts } from "./layout.ts";

/**
 * A staff badge's display object on the canvas (ADR-112 decision 4.4, ADR-111 decision 18): the art from the icon atlas
 * and nothing else, since there is no glyph look; a leader is told apart by its gold art and draws no frame. It is made
 * only for a plate whose character first wears a badge, since a few dozen of 2,500 do, and kept with the plate; a badge
 * is painted only when its look, its place or the way it is drawn changes, never on a frame that changes none.
 */

/**
 * The badge's objects: the art's image.
 */
export interface BadgeObjects {
  readonly art: Phaser.GameObjects.Image;
}

/**
 * Makes a badge's objects in the UI layer, hidden.
 *
 * @param context The UI layer and the scene.
 * @returns The objects.
 */
export function createBadgeObjects(context: WorldUiContext): BadgeObjects {
  const art = context.add(new Phaser.GameObjects.Image(context.scene, 0, 0, "__DEFAULT"));
  art.setOrigin(0, 0).setVisible(false);
  return { art };
}

/**
 * Paints a badge's art, centred level with the flag mark, from the smooth texture where it is downscaled and the nearest
 * one where it is whole; with no frame, nothing is drawn.
 *
 * @param objects The badge's objects.
 * @param parts Where its art stands (`badgeParts`).
 * @param artFrame The icon atlas frame of its art, or null while there is none.
 */
export function paintBadge(objects: BadgeObjects, parts: BadgeParts, artFrame: string | null): void {
  const { art } = objects;
  if (artFrame === null) {
    art.setVisible(false);
    return;
  }
  art.setTexture(parts.smooth ? ICON_SMOOTH_TEXTURE : ICON_TEXTURE, artFrame).setPosition(parts.artX, parts.artY).setScale(parts.artDisplay).setVisible(true);
}

/**
 * Hides a badge's objects.
 *
 * @param objects The badge's objects.
 */
export function hideBadge(objects: BadgeObjects): void {
  objects.art.setVisible(false);
}
